import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import type { HierarchyLabelsResponse } from '~/server_shared/openapi/data-contracts';
import { getNotifyUserMessage } from '~/Utils/errors/notifyUserMessage';

import { reportAxiosError } from '../../../Utils';

import type { UseHierarchyTreeArgs } from './useHierarchyTree';

export interface UseHierarchyLabelsResult {
  labels: Record<string, string>;
  /**
   * A read is in flight for the current inputs. A view that renders stored selections shows a
   * placeholder for this window rather than the raw ID, which would otherwise flash before the
   * label replaces it. False once the read settles either way: a failure leaves the ID visible.
   */
  isLoading: boolean;
}

export function useHierarchyLabels({
  groupId,
  claimId,
  policy,
  valueIds,
}: Omit<UseHierarchyTreeArgs, 'enabled'> & { valueIds: string[] }): UseHierarchyLabelsResult {
  const valueIdsKey = JSON.stringify([...new Set(valueIds)].sort());
  // Serialized only when an input changes, as in `useHierarchyTree`.
  const request = useMemo(
    () =>
      JSON.stringify({
        group_id: groupId,
        ...(claimId === undefined ? { policy } : { claim_id: claimId }),
        value_ids: JSON.parse(valueIdsKey) as string[],
      }),
    [groupId, claimId, policy, valueIdsKey]
  );
  const [state, setState] = useState<{ request: string; labels: Record<string, string> }>({ request: '', labels: {} });
  const hasValues = valueIds.length > 0;

  useEffect(() => {
    if (!hasValues) return undefined;
    let cancelled = false;
    void axios
      .post<HierarchyLabelsResponse>('/api/v1/hierarchy/labels', JSON.parse(request))
      .then(({ data }) => {
        if (!cancelled) setState({ request, labels: data.labels });
      })
      .catch(async (error) => {
        if (getNotifyUserMessage(error) === null) {
          await reportAxiosError(error);
        }
        // The stored ID remains visible when CMS cannot resolve a label.
        if (!cancelled) setState({ request, labels: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [request, hasValues]);

  const isSettled = state.request === request;
  return {
    labels: isSettled && hasValues ? state.labels : {},
    isLoading: hasValues && !isSettled,
  };
}
